import { defineStore } from 'pinia'
import { useQueryCache } from '@pinia/colada'
import * as Sentry from '@sentry/vue'
import { setLocale } from '@/locales'
import { pushService } from '@/features/platform/push/push.service'
import { authService } from './auth.service'

export const useAuthStore = defineStore('auth', () => {
  const generalStore = useGeneralStore()
  const creditsStore = useCreditsStore()
  const queryCache = useQueryCache()

  const { data: allCategories } = useCategoriesQuery()

  // State
  const user = ref<TUser | null>(null)
  const seniorProfile = ref<TSeniorProfile | null>(null)
  const volunteerProfile = ref<TVolunteerProfile | null>(null)
  const authFlowLocale = ref<TSupportedLocale | null>(null)

  const PROFILE_CONFIG_BY_ROLE = {
    senior: {
      profile: seniorProfile,
      getProfile: getSeniorProfile,
      updateProfile: updateSeniorProfile
    },
    volunteer: {
      profile: volunteerProfile,
      getProfile: getVolunteerProfile,
      updateProfile: updateVolunteerProfile
    }
  }

  const userProfile = computed({
    get () {
      if (!user.value) {
        return null
      }
      return PROFILE_CONFIG_BY_ROLE[user.value.role].profile.value
    },
    set (value: TSeniorProfile | TVolunteerProfile) {
      if (!user.value) {
        return
      }
      PROFILE_CONFIG_BY_ROLE[user.value.role].profile.value = value
    }
  })

  const userLocation = computed<IAppLocation>(() => {
    return {
      location: userProfile.value!.homeLocation,
      locationLat: userProfile.value!.homeLat,
      locationLng: userProfile.value!.homeLng
    }
  })

  async function getUserProfile () {
    await PROFILE_CONFIG_BY_ROLE[user.value!.role].getProfile()

    if (authFlowLocale.value) {
      updateUserLocale(authFlowLocale.value)
      authFlowLocale.value = null
    } else if (userProfile.value?.locale) {
      setLocale(userProfile.value.locale as TSupportedLocale)
    }

    pushService.refreshSubscription().catch((err) => {
      console.error('Failed to refresh push subscription', err)
    })
  }

  // Functions
  async function getUser () {
    try {
      user.value = await authService.getUser()

      if (user.value?.role) {
        await getUserProfile()

        if (user.value?.id) {
          Sentry.setUser({
            id: user.value.id,
            username: userProfile.value?.fullName,
            phone: user.value.phone
          })
        }
      }
    } catch (error: any) {
      if (error?.status !== 404) {
        throw error
      }
    }
  }

  async function getSeniorProfile () {
    seniorProfile.value = await authService.getSeniorProfile()
  }

  async function getVolunteerProfile () {
    volunteerProfile.value = await authService.getVolunteerProfile()
  }

  async function updateSeniorProfile (data: TSeniorUpdateProfileRequest, signal?: AbortSignal) {
    await authService.updateSeniorProfile(data, signal)

    seniorProfile.value = {
      ...seniorProfile.value!,
      ...data
    }
  }

  async function updateVolunteerProfile (data: TVolunteerUpdateProfileRequest, signal?: AbortSignal) {
    await authService.updateVolunteerProfile(data, signal)

    const { categoryIds, ...updatedData } = data
    let categories = volunteerProfile.value!.categories as TServiceCategory[]

    if (categoryIds) {
      categories = allCategories.value?.filter(category => categoryIds.includes(category.id)) ?? []
    }

    volunteerProfile.value = {
      ...volunteerProfile.value!,
      ...updatedData,
      categories
    }
  }

  function updateUserProfile (
    data: TSeniorUpdateProfileRequest | TVolunteerUpdateProfileRequest,
    signal?: AbortSignal
  ) {
    return PROFILE_CONFIG_BY_ROLE[user.value!.role].updateProfile(data, signal)
  }

  function updateUserLocale (locale: TSupportedLocale, signal?: AbortSignal) {
    setLocale(locale)

    return updateUserProfile({ locale }, signal)
  }

  async function updateUserPhoneNumber (phone: string) {
    await authService.updateUserPhoneNumber(phone)
    user.value!.phone = phone
  }

  async function fetchInitialInfo () {
    await getUser()

    if (!user.value) {
      return
    }

    return Promise.all([
      generalStore.fetchAppSettings(),
      creditsStore.fetchCreditInfo()
    ])
  }

  // `skipBackendCleanup` is set by the 401 interceptor — skips the BE push DELETE
  // that would itself 401 and re-trigger this path.
  async function logOut ({ skipBackendCleanup = false }: { skipBackendCleanup?: boolean } = {}) {
    try {
      await (skipBackendCleanup ? pushService.unsubscribeLocally() : pushService.unsubscribe())
    } catch (err) {
      console.error('Failed to unsubscribe from push on logout', err)
    }

    try {
      await authService.logOut()
    } catch (err) {
      console.error('Failed to sign out of Supabase', err)
    }

    user.value = null
    seniorProfile.value = null
    volunteerProfile.value = null
    creditsStore.creditInfo = null

    // Drop Pinia Colada cache so the next login doesn't briefly render the previous user's data.
    queryCache.cancelQueries()
    for (const entry of queryCache.getEntries()) {
      queryCache.remove(entry)
    }

    Sentry.setUser(null)
  }

  return {
    // State
    user,
    seniorProfile,
    volunteerProfile,
    userLocation,
    userProfile,
    authFlowLocale,

    // Functions
    getUser,
    getSeniorProfile,
    getVolunteerProfile,
    updateSeniorProfile,
    updateVolunteerProfile,
    updateUserProfile,
    updateUserLocale,
    updateUserPhoneNumber,
    logOut,
    fetchInitialInfo
  }
})
